// packages/core/src/learning/schools/factor-investing.ts
// ─────────────────────────────────────────────────────────────────────────
// SchoolExhibitEntry — authored to match boglehead-indexing.ts (gold standard).
// ID-bound fields (figures / featuredLessonIds / debateIds) left [] for the
// post-authoring cross-reference pass. Worldview named 'jim-simons'
// (valid FigureId) — wire into `figures` during the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const FACTOR_INVESTING_EXHIBIT: SchoolExhibitEntry = {
	id: 'factor-investing',
	tagline:
		'Factor investing believes some return patterns deserve rules, not vibes.',
	worldviewSummary:
		'Factor investing is paying attention to repeated patterns in markets, like value, size, quality, momentum, and profitability. This lens protects you from building an investing story around one favorite company, one hunch, or one recent winner. It accepts tracking error, long dry spells, complexity, and the pain of looking wrong for years. The blind spot is that evidence can make a strategy feel cleaner and safer than the lived experience actually is.',
	coreBeliefs: [
		'Factor investing believes some market patterns can be studied instead of guessed.',
		'Factor investing believes rules can protect investors from story driven decisions.',
		'Factor investing believes evidence matters more than confidence.',
		'Factor investing believes patience is part of the strategy, not a bonus trait.',
		'Factor investing believes tracking error is the emotional cost of being different.',
		'Factor investing believes a factor only matters if you can survive owning it when it disappoints.',
	],
	warnsAgainst: [
		'Building a portfolio around stories, hunches, or recent winners.',
		'Assuming a backtest will feel easy in real life.',
		'Confusing evidence with certainty.',
		'Abandoning a strategy the moment it looks different from the market.',
	],
	helps: 'This lens helps people who want a rules based investing philosophy with more structure than broad indexing, but less personality worship than stock picking. It may not fit someone who wants maximum simplicity, low maintenance, or a strategy that always feels comfortable.',
	figures: [],
	canon: [
		{
			title: 'Fama French factor research',
			oneLineIdea:
				'Markets have shown patterns around value, size, and other traits, but those patterns are not promises.',
		},
		{
			title: 'Momentum factor',
			oneLineIdea:
				'Recent strength can sometimes persist, but living with reversals is part of the tradeoff.',
		},
		{
			title: 'Quality factor',
			oneLineIdea:
				'Some investors prefer businesses with stronger balance sheets, profitability, and durability.',
		},
		{
			title: 'Tracking error',
			oneLineIdea:
				'A factor strategy can trail the market for long stretches, which tests whether the investor meant it.',
		},
		{
			title: 'Evidence based investing',
			oneLineIdea:
				'The point is not to predict perfectly, it is to build rules from data instead of emotion.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-factor-investing',
	debateIds: [],
	applyActions: [
		{
			label: 'Compare factor discipline against your real investing behavior',
			action: {
				type: 'noToolAction',
				label: 'Check your behavior rules',
				prompt: 'Write what would make you abandon a strategy, then ask whether that rule is emotional or evidence based.',
			},
		},
		{
			label: 'Test how steady contributions matter more than clever tilts',
			action: {
				type: 'openSimulator',
				label: 'Simulate steady investing',
				scenarioType: 'custom',
			},
		},
		{
			label: 'See whether your savings rate matters more than strategy complexity',
			action: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
		},
		{
			label: 'Ground the strategy in your current money picture',
			action: {
				type: 'openDashboard',
				label: 'Check your money picture',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Factor investing asks if your strategy has evidence, or just a good story.',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of smart investing is looking wrong for years.',
			kind: 'hook',
		},
		{
			line: 'Have you ever abandoned a strategy because it was bad, or because it felt bad?',
			kind: 'interview-question',
		},
		{
			line: 'What investing rule would you still follow after three boring years?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk turns investing philosophies into tradeoffs you can actually understand.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Factor investing shows why evidence does not remove behavior risk.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show a factor strategy trailing the market, then ask who still believes in the rules.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask your audience if they want evidence, simplicity, or the comfort of following the crowd.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
